import type { Dispatch, SetStateAction } from 'react';
import {
  AddOutlined,
  ArrowBackOutlined,
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  DeleteOutlineOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import type {
  ChecklistTemplate,
  ChecklistTemplateVersionInput,
  ChecklistTemplateWorkerTarget,
  ChecklistTemplateWorkerType,
} from './model';
import {
  ChoiceControl,
  EditPage,
  FormTextField,
  SectionCard,
  ValueBadge,
} from '../../design-system';

export type ChecklistTemplateDraftItem = Omit<ChecklistTemplateVersionInput['items'][number], 'dueOffsetDays'> & {
  clientKey: string;
  dueOffsetDays: string;
};

export type ChecklistTemplateDraft = ChecklistTemplateWorkerTarget & Pick<ChecklistTemplateVersionInput, 'descriptor' | 'lifecycleType'> & {
  items: ChecklistTemplateDraftItem[];
};

function clientKey() {
  return crypto.randomUUID();
}

export function newChecklistTemplateDraftItem(): ChecklistTemplateDraftItem {
  return {
    clientKey: clientKey(),
    descriptor: '',
    executionType: 'MANUAL',
    dueOffsetDays: '0',
    required: true,
  };
}

export function checklistTemplateDraft(template?: ChecklistTemplate): ChecklistTemplateDraft {
  if (!template) return {
    descriptor: '',
    lifecycleType: 'ONBOARDING',
    workerScope: 'WORKER_TYPE',
    workerType: 'EMPLOYEE',
    items: [newChecklistTemplateDraftItem()],
  };
  const base = {
    descriptor: template.descriptor,
    lifecycleType: template.lifecycleType,
    items: template.items.map((item) => ({ ...item, dueOffsetDays: String(item.dueOffsetDays), clientKey: item.id || clientKey() })),
  };
  return template.workerScope === 'ALL'
    ? { ...base, workerScope: 'ALL' }
    : { ...base, workerScope: 'WORKER_TYPE', workerType: template.workerType };
}

export function checklistTemplateInput(draft: ChecklistTemplateDraft): ChecklistTemplateVersionInput {
  const base = {
    descriptor: draft.descriptor.trim(),
    lifecycleType: draft.lifecycleType,
    items: draft.items.map(({ clientKey: _clientKey, dueOffsetDays, ...item }) => ({
      ...item,
      descriptor: item.descriptor.trim(),
      integrationTarget: item.integrationTarget || undefined,
      dueOffsetDays: Number(dueOffsetDays),
    })),
  };
  return draft.workerScope === 'ALL'
    ? { ...base, workerScope: 'ALL' }
    : { ...base, workerScope: 'WORKER_TYPE', workerType: draft.workerType };
}

export function checklistTemplateValidationMessage(value: ChecklistTemplateDraft) {
  const descriptor = value.descriptor.trim();
  if (!descriptor) return '템플릿 이름을 입력해 주세요.';
  if (Array.from(descriptor).length > 120) return '템플릿 이름은 120자 이하로 입력해 주세요.';
  if (value.items.length === 0) return '체크리스트 항목을 하나 이상 추가해 주세요.';
  if (value.items.length > 50) return '체크리스트 항목은 최대 50개까지 만들 수 있습니다.';
  for (const [index, item] of value.items.entries()) {
    if (!item.descriptor.trim()) return `${index + 1}번 항목의 이름을 입력해 주세요.`;
    if (Array.from(item.descriptor.trim()).length > 160) return `${index + 1}번 항목의 이름은 160자 이하로 입력해 주세요.`;
    const dueOffsetDays = Number(item.dueOffsetDays);
    if (!item.dueOffsetDays.trim() || !Number.isInteger(dueOffsetDays) || dueOffsetDays < -365 || dueOffsetDays > 365) {
      return `${index + 1}번 항목의 기한은 기준일 전후 365일 이내로 입력해 주세요.`;
    }
  }
  return '';
}

function integrationLabel(target?: string) {
  if (target === 'GOOGLE_WORKSPACE') return 'Google Workspace 연동';
  if (target === 'SLACK') return 'Slack 연동';
  return '기존 연동 작업';
}

type ChecklistTemplateEditorProps = {
  value: ChecklistTemplateDraft;
  editing?: ChecklistTemplate;
  onChange: Dispatch<SetStateAction<ChecklistTemplateDraft>>;
  onClose: () => void;
  onSubmit: () => void;
  saving: boolean;
  error?: string;
};

export function ChecklistTemplateEditor({
  value,
  editing,
  onChange,
  onClose,
  onSubmit,
  saving,
  error,
}: ChecklistTemplateEditorProps) {
  const validationMessage = checklistTemplateValidationMessage(value);
  const updateItem = (index: number, patch: Partial<ChecklistTemplateDraftItem>) => {
    onChange((current) => ({
      ...current,
      items: current.items.map((item, currentIndex) => currentIndex === index ? { ...item, ...patch } : item),
    }));
  };
  const moveItem = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= value.items.length) return;
    if (value.items[index].executionType === 'INTEGRATION' || value.items[target].executionType === 'INTEGRATION') return;
    onChange((current) => {
      const items = [...current.items];
      [items[index], items[target]] = [items[target], items[index]];
      return { ...current, items };
    });
  };
  const removeItem = (index: number) => {
    if (value.items[index].executionType === 'INTEGRATION') return;
    onChange((current) => ({ ...current, items: current.items.filter((_, currentIndex) => currentIndex !== index) }));
  };

  return <EditPage
    title={editing ? '체크리스트 템플릿 새 버전' : '새 체크리스트 템플릿'}
    help={editing ? '저장하면 기존 체크리스트는 그대로 유지되고, 새 버전이 이후 체크리스트에 사용됩니다.' : undefined}
    width="standard"
    utilities={<Button startIcon={<ArrowBackOutlined />} disabled={saving} onClick={onClose}>{editing ? '템플릿 상세' : '템플릿 목록'}</Button>}
    editActions={<>
      <Typography variant="caption" color={validationMessage ? 'error' : 'text.secondary'} sx={{ flex: '1 1 260px' }}>
        {validationMessage || `${value.items.length}개 항목`}
      </Typography>
      <Button disabled={saving} onClick={onClose}>취소</Button>
      <Button variant="contained" disabled={saving || Boolean(validationMessage)} onClick={onSubmit}>
        {saving ? '저장 중…' : editing ? '새 버전 활성화' : '템플릿 저장'}
      </Button>
    </>}
  >
    <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          <SectionCard title="기본 정보">
            <Box sx={{ p: 1.75, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 1.5 }}>
              <FormTextField
                required
                label="템플릿 이름"
                value={value.descriptor}
                onChange={(event) => onChange({ ...value, descriptor: event.target.value })}
              />
              <FormTextField
                select
                label="구분"
                value={value.lifecycleType}
                disabled={Boolean(editing)}
                onChange={(event) => onChange({ ...value, lifecycleType: event.target.value as ChecklistTemplateDraft['lifecycleType'] })}
              >
                <MenuItem value="ONBOARDING"><ValueBadge label="온보딩" /></MenuItem>
                <MenuItem value="OFFBOARDING"><ValueBadge label="오프보딩" /></MenuItem>
              </FormTextField>
              <FormTextField
                select
                label="대상"
                value={value.workerScope === 'ALL' ? 'ALL' : value.workerType ?? ''}
                disabled={Boolean(editing)}
                onChange={(event) => {
                  const target = event.target.value;
                  if (target === 'ALL') {
                    const { workerType: _workerType, ...current } = value;
                    onChange({ ...current, workerScope: 'ALL' });
                    return;
                  }
                  onChange({ ...value, workerScope: 'WORKER_TYPE', workerType: target as ChecklistTemplateWorkerType });
                }}
              >
                <MenuItem value="ALL"><ValueBadge label="전체 구성원" /></MenuItem>
                <MenuItem value="EMPLOYEE"><ValueBadge label="임직원" /></MenuItem>
                <MenuItem value="CONTINGENT_WORKER"><ValueBadge label="외부 인력" /></MenuItem>
              </FormTextField>
            </Box>
          </SectionCard>

          <SectionCard
            title={`체크리스트 항목 ${value.items.length}개`}
            action={<Button
              size="small"
              startIcon={<AddOutlined />}
              disabled={saving || value.items.length >= 50}
              onClick={() => onChange((current) => ({ ...current, items: [...current.items, newChecklistTemplateDraftItem()] }))}
            >항목 추가</Button>}
          >
            <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
              {value.items.map((item, index) => {
                const integration = item.executionType === 'INTEGRATION';
                const canMoveUp = !integration && index > 0 && value.items[index - 1].executionType !== 'INTEGRATION';
                const canMoveDown = !integration && index < value.items.length - 1 && value.items[index + 1].executionType !== 'INTEGRATION';
                return <Box key={item.clientKey} sx={{ p: 1.5 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ alignItems: { md: 'center' } }}>
                    <Chip size="small" label={`${index + 1}`} sx={{ alignSelf: { xs: 'flex-start', md: 'auto' } }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <FormTextField
                        required
                        fullWidth
                        label="항목 이름"
                        value={item.descriptor}
                        disabled={integration}
                        onChange={(event) => updateItem(index, { descriptor: event.target.value })}
                      />
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', md: 265 } }}>
                      <FormTextField select fullWidth label="기한 기준" value={item.dueOffsetDays.startsWith('-') ? 'BEFORE' : Number(item.dueOffsetDays) > 0 || item.dueOffsetDays === '+' ? 'AFTER' : 'SAME'} disabled={integration || saving}
                        onChange={(event) => updateItem(index, { dueOffsetDays: event.target.value === 'SAME' ? '0' : String((event.target.value === 'BEFORE' ? -1 : 1) * Math.max(1, Math.abs(Number(item.dueOffsetDays) || 1))) })}>
                        <MenuItem value="BEFORE">기준일 전</MenuItem><MenuItem value="SAME">기준일 당일</MenuItem><MenuItem value="AFTER">기준일 후</MenuItem>
                      </FormTextField>
                      {Number(item.dueOffsetDays) !== 0 && <FormTextField fullWidth type="number" label="일수" value={Math.abs(Number(item.dueOffsetDays)) || ''} disabled={integration || saving} unit="일" slotProps={{ htmlInput: { min: 1, max: 365, step: 1 } }}
                        onChange={(event) => updateItem(index, { dueOffsetDays: event.target.value ? String((item.dueOffsetDays.startsWith('-') ? -1 : 1) * Math.abs(Number(event.target.value))) : item.dueOffsetDays.startsWith('-') ? '-' : '+' })} />}
                    </Stack>
                    <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center', justifyContent: { xs: 'flex-end', md: 'initial' } }}>
                      <IconButton size="small" title="위로 이동" aria-label={`${item.descriptor || `${index + 1}번 항목`} 위로 이동`} disabled={!canMoveUp} onClick={() => moveItem(index, -1)}><ArrowUpwardOutlined /></IconButton>
                      <IconButton size="small" title="아래로 이동" aria-label={`${item.descriptor || `${index + 1}번 항목`} 아래로 이동`} disabled={!canMoveDown} onClick={() => moveItem(index, 1)}><ArrowDownwardOutlined /></IconButton>
                      <IconButton size="small" title="항목 삭제" color="error" aria-label={`${item.descriptor || `${index + 1}번 항목`} 삭제`} disabled={integration || value.items.length === 1} onClick={() => removeItem(index)}><DeleteOutlineOutlined /></IconButton>
                    </Stack>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1, alignItems: { sm: 'center' } }}>
                    <ValueBadge label={integration ? integrationLabel(item.integrationTarget) : '담당자 처리'} />
                    <ChoiceControl
                      kind="checkbox"
                      checked={item.required}
                      disabled={integration}
                      onCheckedChange={(checked) => updateItem(index, { required: checked })}
                      label="필수 항목"
                    />
                    {integration && <Typography variant="caption" color="text.secondary">기존 연동 항목은 현재 설정을 보존합니다. 새 연동 항목 추가는 지원하지 않습니다.</Typography>}
                  </Stack>
                </Box>;
              })}
            </Stack>
          </SectionCard>
    </Stack>
  </EditPage>;
}
